import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { ProductLogo } from '@qwickapps/react-framework';
import { MustacheTemplateProvider } from '@qwickapps/schema';
import { useDocument } from '../contexts/DocumentContext';
import { createDefaultIcon } from '../data/defaultLetterhead';

export const LetterheadHeader: React.FC = () => {
  const { letterheadConfig } = useDocument();

  const templateProvider = useMemo(() => new MustacheTemplateProvider(), []);

  if (!letterheadConfig.enabled) {
    return null;
  }

  // If custom template is provided, use it
  if (letterheadConfig.headerTemplate) {
    const hasLogoPlaceholder = letterheadConfig.headerTemplate.includes('{{logo}}');

    const templateContext = {
      companyName: letterheadConfig.companyName,
      tagline: letterheadConfig.tagline,
      address: letterheadConfig.address,
      email: letterheadConfig.email,
      phone: letterheadConfig.phone,
      website: letterheadConfig.website,
      date: new Date().toLocaleDateString(),
      // Replace {{logo}} with a unique marker that we can replace with the actual component
      logo: hasLogoPlaceholder ? '<span data-logo-placeholder="true"></span>' : '',
    };

    let renderedHtml = templateProvider.resolve(letterheadConfig.headerTemplate, templateContext);

    return (
      <Box
        sx={{
          p: 2,
          m: 0,
          borderBottom: '2px solid',
          borderColor: 'primary.main',
          backgroundColor: 'background.paper',
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          height: '100%',
          boxSizing: 'border-box',
        }}
      >
        {hasLogoPlaceholder ? (
          // If template has {{logo}}, split by placeholder and insert ProductLogo
          <>
            {renderedHtml.split('<span data-logo-placeholder="true"></span>').map((part, index, arr) => (
              <React.Fragment key={index}>
                {part && <Box dangerouslySetInnerHTML={{ __html: part }} />}
                {index < arr.length - 1 && (
                  <ProductLogo
                    name={letterheadConfig.logoText || letterheadConfig.companyName}
                    size={letterheadConfig.logoSize}
                    variant={letterheadConfig.logoVariant}
                    icon={letterheadConfig.customIcon}
                  />
                )}
              </React.Fragment>
            ))}
          </>
        ) : (
          <Box dangerouslySetInnerHTML={{ __html: renderedHtml }} sx={{ width: '100%' }} />
        )}
      </Box>
    );
  }

  // Get custom icon - either from logoUrl or default icon
  const logoIcon = useMemo(
    () =>
      letterheadConfig.logoUrl
        ? React.createElement('img', {
            src: letterheadConfig.logoUrl,
            alt: letterheadConfig.companyName,
            style: {
              display: 'block',
              height: '32px',
              width: '32px',
            },
          })
        : createDefaultIcon(),
    [letterheadConfig.logoUrl, letterheadConfig.companyName]
  );

  // Get alignment (default to left)
  const alignment = letterheadConfig.headerAlign || 'left';
  const alignItems = alignment === 'left' ? 'flex-start' : alignment === 'right' ? 'flex-end' : 'center';

  // Default header layout: logo + tagline with configurable alignment
  return (
    <Box
      sx={{
        p: 2,
        m: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems,
        justifyContent: 'center',
        borderBottom: '2px solid',
        borderColor: 'primary.main',
        backgroundColor: 'background.paper',
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
      }}
    >
      <ProductLogo
        name={letterheadConfig.logoText || letterheadConfig.companyName}
        size={letterheadConfig.logoSize}
        variant={letterheadConfig.logoVariant}
        icon={logoIcon}
      />
      {letterheadConfig.tagline && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
          {letterheadConfig.tagline}
        </Typography>
      )}
    </Box>
  );
};
